import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  HelpDocsJwtControllerService,
  HelpDocsJwtResponseDTO
} from '@libs/api';
import { environment } from '@portal/environments/environment';
import { WindowService } from './window.service';

// HelpDocs "drop the JWT as a cookie" endpoint; hitting it with an expired
// token clears the support.measured.com session cookies.
// https://support.helpdocs.io/article/g7iasnwau5-restricting-your-help-docs-with-custom-jwt-sso#dropping_the_jwt_as_a_cookie
const HELPDOCS_CLEAR_URL = 'https://support.measured.com/login/jwt/token';

@Injectable({
  providedIn: 'root'
})
export class HelpDocsJwtService {
  private readonly helpDocsApi = inject(HelpDocsJwtControllerService);
  private readonly windowService = inject(WindowService);

  constructor() {
    this.helpDocsApi.configuration.withCredentials = true;
  }

  private isHelpDocsSsoActive(): boolean {
    return environment.production;
  }

  /**
   * Calls API to generate HelpDocs JWT and set cookie via Set-Cookie header
   */
  setHelpDocsJwt(): Observable<HelpDocsJwtResponseDTO> {
    if (!this.isHelpDocsSsoActive()) {
      return of({} as HelpDocsJwtResponseDTO);
    }

    return this.helpDocsApi.helpDocsJwtControllerGenerateHelpDocsJwt();
  }

  /**
   * Clears the HelpDocs session on logout by firing an expired token at the
   * clear endpoint as a same-site image beacon. No-op outside production.
   */
  clearHelpDocsJwt(): Observable<void> {
    if (!this.isHelpDocsSsoActive()) {
      return of(undefined);
    }

    return this.helpDocsApi
      .helpDocsJwtControllerGenerateHelpDocsJwt({ clear: true })
      .pipe(
        switchMap((response: HelpDocsJwtResponseDTO) =>
          response.expiredToken
            ? this.clearBeacon(response.expiredToken)
            : of(undefined)
        )
      );
  }

  private clearBeacon(expiredToken: string): Observable<void> {
    const url = `${HELPDOCS_CLEAR_URL}?hd_jwt=${encodeURIComponent(
      expiredToken
    )}`;
    return this.windowService.loadBeacon(url);
  }
}
